import React, { useEffect, useRef, useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  PRODUCT_IDS,
  useAccountEntitlements,
  useCurrentAccountId,
  useCurrentProject,
  useCurrentProjectId,
  useCurrentProjectIsTemplate,
  useProjectEntitlements,
  useUser,
  useCurrentProductId,
} from '@adsk/alloy-provider';
import TopNavigation from '@adsk/alloy-top-navigation';

import getProductPickerDividers from './helpers/getProductPickerDividers';
import { initialize as initializePendo } from './pendo';
import ProductPickerHeader from './ProductPickerHeader';
import { useProducts } from './useProducts';
import { useProductsAccessTypes } from './useProductsAccessTypes';

const ConnectedTopNavigation = ({
  showAccountsInHeader,
  onLogout,
  helpLinks,
  isProductRequired,
  isAccountView,
  onProductClick,
  onProjectClick,
  onAccountClick,
  UNSAFE_showBuildersUserProfileModal,
  UNSAFE_isBuildersProfileModalEnabled,
  ...otherProps
}) => {
  const currentProject = useCurrentProject();
  const {
    UNSAFE_projects: projectsAndTemplates,
    loading: projectsLoading,
    error: projectsError,
    retry: projectsRetry,
  } = useProjectEntitlements();
  const { projects, templates } = useMemo(() => {
    const validProjects = projectsAndTemplates?.filter((p) => !!p.defaultUrl);
    return {
      projects: validProjects?.filter((p) => p.classification !== 'template'),
      templates: validProjects?.filter((p) => p.classification === 'template'),
    };
  }, [projectsAndTemplates]);

  const { data: accountsData } = useAccountEntitlements();
  const currentProductId = useCurrentProductId();
  const { user } = useUser();
  const currentAccountId = useCurrentAccountId();
  const currentProjectId = useCurrentProjectId();
  const currentProjectIsTemplate = useCurrentProjectIsTemplate();
  const accounts = accountsData?.accounts;
  const products = useProducts();

  const isPendoInitialized = useRef(false);

  const { data: productsAccessTypesData, loading: productsAccessTypesLoading } =
    useProductsAccessTypes();

  useEffect(() => {
    if (
      !productsAccessTypesLoading &&
      user &&
      (currentProject || isAccountView) &&
      !isPendoInitialized.current
    ) {
      const licenses = productsAccessTypesData;
      const currentProductLicense = licenses?.find(
        ({ productType }) => productType === currentProductId,
      );

      initializePendo({
        user,
        context: {
          is_project_template: currentProjectIsTemplate,
          is_admin: currentProject?.accessLevels?.projectAdministrator,
          is_on_trial: currentProductLicense?.type === 'trial',
          account_id: currentAccountId,
          project_id: currentProjectId,
          project_classification: currentProject?.classification,
          account_name: currentProject?.accountName,
          is_acct_admin: currentProject?.accessLevels?.accountAdministrator,
        },
      });

      isPendoInitialized.current = true;
    }
  }, [
    currentAccountId,
    currentProject,
    currentProjectId,
    currentProjectIsTemplate,
    currentProductId,
    isAccountView,
    productsAccessTypesData,
    productsAccessTypesLoading,
    user,
  ]);

  return (
    <TopNavigation
      accountId={currentAccountId}
      accounts={showAccountsInHeader ? accounts : undefined}
      projectId={currentProjectId}
      UNSAFE_templates={templates}
      UNSAFE_projects={projects}
      UNSAFE_projectsLoading={projectsLoading}
      UNSAFE_projectsError={!!projectsError}
      UNSAFE_projectsRetry={projectsRetry}
      UNSAFE_showBuildersUserProfileModal={UNSAFE_showBuildersUserProfileModal}
      UNSAFE_isBuildersProfileModalEnabled={
        UNSAFE_isBuildersProfileModalEnabled
      }
      currentProject={currentProject}
      currentProductId={currentProductId}
      templateId={currentProjectId}
      products={getProductPickerDividers(products)}
      user={user}
      onLogout={onLogout}
      showHelpMenu
      admin={[
        PRODUCT_IDS.PROJECT_ADMINISTRATION,
        PRODUCT_IDS.ACCOUNT_ADMINISTRATION,
      ].includes(currentProductId)}
      helpLinks={helpLinks}
      showSubscriptionsModal
      showTrialBanner
      isProductRequired={isProductRequired}
      renderCustomProductHeader={
        isAccountView ? undefined : () => <ProductPickerHeader />
      }
      onProductClick={onProductClick}
      onProjectClick={onProjectClick}
      onAccountClick={onAccountClick}
      {...otherProps}
    />
  );
};

ConnectedTopNavigation.propTypes = {
  /** choose between Project Picker Account view or Project View */
  showAccountsInHeader: PropTypes.bool,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** Override default help links */
  helpLinks: TopNavigation.propTypes.helpLinks,
  /** is product required */
  isProductRequired: PropTypes.bool,
  /** is account view */
  isAccountView: PropTypes.bool,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
  /** Triggers opening the Builders Network Profile Modal */
  UNSAFE_showBuildersUserProfileModal: PropTypes.func,
  /** When BuildersNetworkProfile is enabled, it updates the TopNav Profile dropdown options to include button for opening the BN Modal and hides the Profile settings button*/
  UNSAFE_isBuildersProfileModalEnabled: PropTypes.bool,
};

export { ConnectedTopNavigation };
